/**
 * FundRaisingHubModal
 * OPO hub for the Fund Raising department (13th department).
 * Tabs: Command Center | Government | Private | Applications | Agents | Knowledge Base | Analytics
 */
import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import FundRaisingChannelPanel from './FundRaisingChannelPanel';
import FundRaisingApplicationPanel from './FundRaisingApplicationPanel';
import FundRaisingAgentsPanel from './FundRaisingAgentsPanel';
import './FundRaisingHubModal.css';

const API = '/api/opo/fundraising';

const TABS = [
  { id: 'command',      label: 'Command Center' },
  { id: 'government',  label: 'Government' },
  { id: 'private',     label: 'Private' },
  { id: 'applications',label: 'Applications' },
  { id: 'agents',      label: 'Agents' },
  { id: 'knowledge',   label: 'Knowledge Base' },
  { id: 'analytics',   label: 'Analytics' },
];

const STATUS_COLORS = {
  active: '#10b981',
  'check-intake': '#f59e0b',
  watchlist: '#6b7280',
  closed: '#ef4444',
  drafting: '#6366f1',
  review: '#f59e0b',
  ready: '#10b981',
  submitted: '#3b82f6',
  pending: '#8b5cf6',
  approved: '#10b981',
  rejected: '#ef4444',
};

const statusDot = (s) => (
  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[s] || '#6b7280', marginRight: 6, flexShrink: 0 }} />
);

function fmt(n) { return n != null ? `$${Number(n).toLocaleString('en-CA')}` : '—'; }
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-CA') : '—'; }
function fmtDateTime(s) {
  return s ? new Date(s).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

// ── Overall Summary (pinned Qwen 3.5 35B via Ollama) ────────────────────────
function OverallSummarySection() {
  const [summaryMd, setSummaryMd]   = useState(null);
  const [meta, setMeta]             = useState({ generated_at: null, model: null, channel_count: null });
  const [loadingCache, setLoadingCache] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError]           = useState(null);
  const pollRef = React.useRef(null);

  const loadCached = useCallback((quiet = false) => {
    if (!quiet) setLoadingCache(true);
    fetch(`${API}/overall-summary`)
      .then(r => r.json())
      .then((d) => {
        setSummaryMd(d.summary_md || null);
        setMeta({
          generated_at: d.generated_at || null,
          model: d.model || null,
          channel_count: d.channel_count ?? null,
        });
        // Sync generating flag with server state
        if (d.generating) {
          setGenerating(true);
        } else {
          setGenerating(false);
          if (d.gen_error) setError(d.gen_error);
          // Stop polling once generation is done
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      })
      .catch(() => {})
      .finally(() => { if (!quiet) setLoadingCache(false); });
  }, []);

  useEffect(() => {
    loadCached();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadCached]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(() => loadCached(true), 8000);
  }, [loadCached]);

  const handleGenerate = () => {
    setError(null);
    fetch(`${API}/overall-summary/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: '{}',
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (d.generating || d.started) {
          setGenerating(true);
          startPolling();
        } else if (d.error) {
          setError(d.error);
        }
      })
      .catch((e) => setError(e.message || String(e)));
  };

  return (
    <div className="fr-panel fr-overall-summary">
      <div className="fr-overall-summary-header">
        <h3 className="fr-panel-title" style={{ marginBottom: 0 }}>Overall Summary</h3>
        <div className="fr-overall-summary-actions">
          <button
            type="button"
            className="fr-btn"
            onClick={handleGenerate}
            disabled={generating}
            title="Reads every Government and Private channel extract (OVERVIEW + requirements + analyst scores) and asks the pinned Qwen 3.5 35B for a department-wide brief. Returns instantly; polls automatically."
          >
            {generating
              ? <span>⏳ Generating… <span style={{ fontSize: 11, fontWeight: 400 }}>auto-refreshing</span></span>
              : 'Generate with Qwen 3.5 35B'}
          </button>
          <button type="button" className="fr-btn-outline" onClick={() => loadCached()} disabled={generating || loadingCache}>
            Refresh
          </button>
        </div>
      </div>

      <p className="fr-muted fr-overall-summary-intro">
        One narrative across all programs: what to pursue soon, what is a weak fit, a practical path forward, and what the Application Builder still needs before Compliance can validate drafts.
        Model: <code className="fr-code-inline">{meta.model || 'qwen3.5:35b'}</code> (local Ollama, runs in background — panel updates automatically).
        {meta.generated_at ? ` Last saved: ${fmtDateTime(meta.generated_at)}` : ''}
        {meta.channel_count != null ? ` · ${meta.channel_count} channels` : ''}.
      </p>

      {generating && (
        <div className="fr-overall-summary-generating">
          <div className="fr-overall-summary-spinner" />
          <span>Qwen 3.5 35B is reading all program extracts and writing the report — typically 3–10 min. You can navigate away; the result will be here when you return.</span>
        </div>
      )}

      {error && <div className="fr-overall-summary-error">{error}</div>}

      {loadingCache && !summaryMd && !generating && <p className="fr-muted">Loading last summary…</p>}
      {!loadingCache && !summaryMd && !generating && !error && (
        <p className="fr-muted">No summary saved yet. Run <strong>Scrape + Score All</strong> on the Agents tab first for richer extracts, then generate here.</p>
      )}

      {summaryMd && (
        <div className="fr-overall-summary-md">
          <ReactMarkdown>{summaryMd}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

// ── Command Center ───────────────────────────────────────────────────────────
function CommandCenter({ overview, onNavigate }) {
  if (!overview) return <div className="fr-loading">Loading overview…</div>;

  const channelCounts = {};
  (overview.channel_counts || []).forEach(r => { channelCounts[r.status] = r.count; });
  const pipelineCounts = {};
  (overview.pipeline_counts || []).forEach(r => { pipelineCounts[r.status] = r.count; });

  return (
    <div className="fr-command-center">
      <div className="fr-stat-grid">
        <div className="fr-stat-card">
          <div className="fr-stat-label">Active Programs</div>
          <div className="fr-stat-value">{channelCounts['active'] || 0}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Watch-listed</div>
          <div className="fr-stat-value fr-stat-amber">{channelCounts['watchlist'] || 0}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Applications in Flight</div>
          <div className="fr-stat-value fr-stat-blue">
            {(pipelineCounts['drafting'] || 0) + (pipelineCounts['review'] || 0) + (pipelineCounts['ready'] || 0) + (pipelineCounts['submitted'] || 0)}
          </div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Funding Secured (CAD)</div>
          <div className="fr-stat-value fr-stat-green">{fmt(overview.funding_secured_cad)}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Unresolved Alerts</div>
          <div className="fr-stat-value fr-stat-red">{(overview.unresolved_alerts || []).length}</div>
        </div>
      </div>

      <div className="fr-two-col">
        <div className="fr-panel">
          <h3 className="fr-panel-title">Active Alerts</h3>
          {(overview.unresolved_alerts || []).length === 0
            ? <p className="fr-muted">No active alerts.</p>
            : (overview.unresolved_alerts || []).slice(0, 5).map(a => (
              <div key={a.id} className="fr-alert-row">
                <span className={`fr-badge fr-badge-${a.severity}`}>{a.severity}</span>
                <span className="fr-alert-title">{a.title || a.summary_md?.slice(0, 80) || 'Alert'}</span>
                <span className="fr-muted fr-alert-date">{fmtDate(a.detected_at)}</span>
              </div>
            ))
          }
        </div>

        <div className="fr-panel">
          <h3 className="fr-panel-title">Applications in Progress</h3>
          {(overview.upcoming_applications || []).length === 0
            ? <p className="fr-muted">No applications in progress.</p>
            : (overview.upcoming_applications || []).map(a => (
              <div key={a.id} className="fr-app-row" onClick={() => onNavigate('applications', a.id)} style={{ cursor: 'pointer' }}>
                {statusDot(a.status)}
                <span className="fr-app-name">{a.name}</span>
                <span className="fr-muted">{a.channel_name}</span>
              </div>
            ))
          }
        </div>
      </div>

      <div className="fr-panel">
        <h3 className="fr-panel-title">Agent Status</h3>
        <div className="fr-agent-status-grid">
          {(overview.agent_status || []).map(a => (
            <div key={a.agent_type} className="fr-agent-status-card">
              <div className="fr-agent-status-name">{a.agent_type}</div>
              <div className={`fr-badge fr-badge-${a.last_status === 'completed' ? 'green' : a.last_status === 'running' ? 'blue' : 'red'}`}>{a.last_status || 'never run'}</div>
              <div className="fr-muted fr-agent-status-time">{a.last_run ? fmtDate(a.last_run) : '—'}</div>
            </div>
          ))}
        </div>
      </div>

      <OverallSummarySection />
    </div>
  );
}

// ── Channel List ─────────────────────────────────────────────────────────────
function ChannelList({ category, onSelect }) {
  const [channels, setChannels] = useState(null);
  const [search, setSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('');

  useEffect(() => {
    fetch(`${API}/channels?category=${encodeURIComponent(category)}`)
      .then(r => r.json())
      .then(d => setChannels(d.channels || []))
      .catch(() => setChannels([]));
  }, [category]);

  if (!channels) return <div className="fr-loading">Loading channels…</div>;

  const regions = [...new Set(channels.map(c => c.region).filter(Boolean))].sort();
  const filtered = channels.filter(c =>
    (!search || c.name.toLowerCase().includes(search.toLowerCase())) &&
    (!regionFilter || c.region === regionFilter)
  );
  const sorted = [...filtered].sort((a, b) => {
    const sa = a.fit_score != null ? Number(a.fit_score) : -1;
    const sb = b.fit_score != null ? Number(b.fit_score) : -1;
    return sb - sa;
  });

  return (
    <div>
      <div className="fr-filters">
        <input className="fr-input" placeholder="Search programs…" value={search} onChange={e => setSearch(e.target.value)} />
        <select className="fr-select" value={regionFilter} onChange={e => setRegionFilter(e.target.value)}>
          <option value="">All regions</option>
          {regions.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      <div className="fr-channel-grid">
        {sorted.map(c => (
          <div key={c.code} className="fr-channel-card" onClick={() => onSelect(c.code)} style={{ cursor: 'pointer' }}>
            <div className="fr-channel-card-header">
              {statusDot(c.status)}
              <span className="fr-channel-name">{c.name}</span>
              {c.region && <span className="fr-badge fr-badge-region">{c.region}</span>}
            </div>
            <div className="fr-channel-meta">
              {c.funding_min_cad || c.funding_max_cad
                ? <span>{fmt(c.funding_min_cad)} – {fmt(c.funding_max_cad)}</span>
                : null}
              {c.intake_model && <span className="fr-muted">{c.intake_model}</span>}
              {c.fit_score != null && (
                <span className={`fr-fit-score fr-fit-${c.fit_score >= 70 ? 'high' : c.fit_score >= 40 ? 'mid' : 'low'}`}>
                  Fit: {Math.round(c.fit_score)}
                </span>
              )}
            </div>
            {c.notes && <div className="fr-channel-notes fr-muted">{c.notes.slice(0, 120)}{c.notes.length > 120 ? '…' : ''}</div>}
          </div>
        ))}
        {filtered.length === 0 && <p className="fr-muted">No programs match current filters.</p>}
      </div>
    </div>
  );
}

// ── Application List ──────────────────────────────────────────────────────────
function ApplicationList({ onSelect }) {
  const [apps, setApps] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    const url = statusFilter ? `${API}/applications?status=${statusFilter}` : `${API}/applications`;
    fetch(url).then(r => r.json()).then(d => setApps(d.applications || [])).catch(() => setApps([]));
  }, [statusFilter]);

  const PIPELINE_STAGES = ['drafting', 'review', 'ready', 'submitted', 'pending', 'approved', 'rejected'];

  if (!apps) return <div className="fr-loading">Loading applications…</div>;

  return (
    <div>
      <div className="fr-filters">
        <select className="fr-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All stages</option>
          {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      <div className="fr-pipeline-view">
        {PIPELINE_STAGES.map(stage => {
          const stageApps = apps.filter(a => a.status === stage);
          if (!stageApps.length && statusFilter && statusFilter !== stage) return null;
          return (
            <div key={stage} className="fr-pipeline-stage">
              <div className="fr-pipeline-stage-header">
                {statusDot(stage)}
                <span className="fr-pipeline-stage-name">{stage.charAt(0).toUpperCase() + stage.slice(1)}</span>
                <span className="fr-pipeline-count">{stageApps.length}</span>
              </div>
              {stageApps.map(a => (
                <div key={a.id} className="fr-pipeline-card" onClick={() => onSelect(a.id)} style={{ cursor: 'pointer' }}>
                  <div className="fr-pipeline-card-name">{a.name}</div>
                  <div className="fr-muted fr-pipeline-channel">{a.channel_name}</div>
                </div>
              ))}
            </div>
          );
        })}
      </div>
      {apps.length === 0 && <p className="fr-muted">No applications yet. Create one from a channel's detail page.</p>}
    </div>
  );
}

// ── Knowledge Base ────────────────────────────────────────────────────────────
function KnowledgeBase() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetch(`${API}/knowledge`).then(r => r.json()).then(d => setItems(d.knowledge || [])).catch(() => setItems([]));
  }, []);

  if (!items) return <div className="fr-loading">Loading knowledge base…</div>;

  const filtered = items.filter(i => !search || i.title.toLowerCase().includes(search.toLowerCase()) || (i.content_md || '').toLowerCase().includes(search.toLowerCase()));

  return (
    <div>
      <div className="fr-filters">
        <input className="fr-input" placeholder="Search knowledge…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      {filtered.length === 0
        ? <p className="fr-muted">Knowledge base is empty. Past applications, reviewer feedback, and patterns will appear here.</p>
        : filtered.map(i => (
          <div key={i.id} className="fr-kb-row">
            <div className="fr-kb-title">{i.title}</div>
            <div className="fr-kb-meta">
              <span className="fr-badge fr-badge-region">{i.source_type}</span>
              {i.channel_name && <span className="fr-muted">{i.channel_name}</span>}
              {i.outcome && <span className={`fr-badge fr-badge-${i.outcome === 'approved' ? 'green' : 'red'}`}>{i.outcome}</span>}
            </div>
            <div className="fr-kb-excerpt fr-muted">{(i.content_md || '').slice(0, 200)}{(i.content_md || '').length > 200 ? '…' : ''}</div>
          </div>
        ))
      }
    </div>
  );
}

// ── Analytics ─────────────────────────────────────────────────────────────────
function Analytics({ overview }) {
  if (!overview) return <div className="fr-loading">Loading…</div>;

  const pipelineCounts = {};
  (overview.pipeline_counts || []).forEach(r => { pipelineCounts[r.status] = r.count; });
  const total = Object.values(pipelineCounts).reduce((a, b) => a + b, 0);
  const approved = pipelineCounts['approved'] || 0;
  const successRate = total > 0 ? Math.round((approved / total) * 100) : null;

  return (
    <div className="fr-analytics">
      <div className="fr-stat-grid">
        <div className="fr-stat-card">
          <div className="fr-stat-label">Total Applications</div>
          <div className="fr-stat-value">{total}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Approved</div>
          <div className="fr-stat-value fr-stat-green">{approved}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Success Rate</div>
          <div className="fr-stat-value">{successRate != null ? `${successRate}%` : '—'}</div>
        </div>
        <div className="fr-stat-card">
          <div className="fr-stat-label">Funding Secured (CAD)</div>
          <div className="fr-stat-value fr-stat-green">{fmt(overview.funding_secured_cad)}</div>
        </div>
      </div>

      <div className="fr-panel">
        <h3 className="fr-panel-title">Pipeline Breakdown</h3>
        {Object.entries(pipelineCounts).map(([stage, count]) => (
          <div key={stage} className="fr-pipeline-bar-row">
            <span className="fr-pipeline-bar-label">{stage}</span>
            <div className="fr-pipeline-bar-track">
              <div className="fr-pipeline-bar-fill" style={{ width: `${Math.round((count / Math.max(total, 1)) * 100)}%`, background: STATUS_COLORS[stage] || '#6b7280' }} />
            </div>
            <span className="fr-pipeline-bar-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
export default function FundRaisingHubModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('command');
  const [overview, setOverview] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);

  const fetchOverview = useCallback(() => {
    fetch(`${API}/overview`)
      .then(r => r.json())
      .then(setOverview)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isOpen) fetchOverview();
    const interval = setInterval(fetchOverview, 60000);
    return () => clearInterval(interval);
  }, [isOpen, fetchOverview]);

  if (!isOpen) return null;

  const handleChannelSelect = (code) => {
    setSelectedChannel(code);
  };

  const handleAppSelect = (id) => {
    setSelectedApp(id);
    setActiveTab('applications');
  };

  const handleNavigate = (tab, id) => {
    setActiveTab(tab);
    if (tab === 'applications' && id) setSelectedApp(id);
  };

  const handleBack = () => {
    setSelectedChannel(null);
    setSelectedApp(null);
  };

  return (
    <div className="fr-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="fr-modal" onClick={e => e.stopPropagation()}>
        <div className="fr-header">
          <div className="fr-header-left">
            <span className="fr-header-icon">💰</span>
            <h2 className="fr-header-title">Fund Raising</h2>
            <span className="fr-header-dept">Department 12 — OptiPrime AI Inc.</span>
          </div>
          <button type="button" className="fr-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="fr-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              type="button"
              className={`fr-tab ${activeTab === t.id ? 'fr-tab-active' : ''}`}
              onClick={() => { setActiveTab(t.id); setSelectedChannel(null); setSelectedApp(null); }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="fr-content">
          {/* Back button when in a drilldown */}
          {(selectedChannel || selectedApp) && (
            <button type="button" className="fr-back-btn" onClick={handleBack}>← Back</button>
          )}

          {/* Command Center */}
          {activeTab === 'command' && !selectedChannel && !selectedApp && (
            <CommandCenter overview={overview} onNavigate={handleNavigate} />
          )}

          {/* Government */}
          {activeTab === 'government' && !selectedChannel && (
            <ChannelList category="Government" onSelect={handleChannelSelect} />
          )}
          {activeTab === 'government' && selectedChannel && (
            <FundRaisingChannelPanel channelCode={selectedChannel} onClose={handleBack} onSelectApp={handleAppSelect} />
          )}

          {/* Private */}
          {activeTab === 'private' && !selectedChannel && (
            <ChannelList category="Private" onSelect={handleChannelSelect} />
          )}
          {activeTab === 'private' && selectedChannel && (
            <FundRaisingChannelPanel channelCode={selectedChannel} onClose={handleBack} onSelectApp={handleAppSelect} />
          )}

          {/* Applications */}
          {activeTab === 'applications' && !selectedApp && (
            <ApplicationList onSelect={setSelectedApp} />
          )}
          {activeTab === 'applications' && selectedApp && (
            <FundRaisingApplicationPanel applicationId={selectedApp} onClose={handleBack} />
          )}

          {/* Agents */}
          {activeTab === 'agents' && <FundRaisingAgentsPanel />}

          {/* Knowledge Base */}
          {activeTab === 'knowledge' && <KnowledgeBase />}

          {/* Analytics */}
          {activeTab === 'analytics' && <Analytics overview={overview} />}
        </div>
      </div>
    </div>
  );
}
